import { useSelector } from 'react-redux';
import { useCallback, useEffect, useState } from 'react';
import { FormViewParams, StateModel } from '../redux/models/state.model';
import {
  clearDialogsStack,
  closeDialog,
  openDialog,
  setDialogContext,
  setDialogViewData,
} from '../redux/reducers/dialogViewsReducer';
import {
  closePageView,
  openPageView as openView,
  setPageViewData,
  setupViewStack,
} from '../redux/reducers/pageViewsReducer';
import { ViewParams, viewParamsToString } from '../models/ViewParams';
import { useAppDispatch } from '../redux/store';
import { FormContextValue } from '../contexts/FormContext';
import { PageAreaBreadcrumbs } from '../components/BreadcrumbsComponent/models/PageAreaBreadcrumbs';
import { Breadcrumb } from '../components/BreadcrumbsComponent/models/Breadcrumb';
import {
  closeSidebarView,
  openSidebar,
  setSidebarContext,
  setSidebarViewData,
} from '../redux/reducers/sidebarViewsReduser';
import { dispatchByActiveView } from '../utils/dispatchByActiveView';

const useViewController = () => {
  const appDispatch = useAppDispatch();

  const dialogs = useSelector((state: StateModel) => state.dialogViewsState);
  const sidebars = useSelector((state: StateModel) => state.sidebarViewsState);
  const viewStack = useSelector((state: StateModel) => state.pageViewStack);

  const [currentView, setCurrentView] = useState<FormViewParams>(null);

  useEffect(() => {
    if (viewStack && viewStack.length) setCurrentView(viewStack[viewStack.length - 1]);
  }, [viewStack]);

  /** Функция для открытия вьюхи в диалоге */
  const openModalView = (viewParams: ViewParams) =>
    appDispatch(openDialog({ viewRequest: viewParams }));

  /** Функция для открытия сайдбара */
  const openSidebarView = (viewParams: ViewParams, location: string) =>
    appDispatch(
      openSidebar({
        viewRequest: viewParams,
        location,
      }),
    );

  /** Универсальный для модалки, фулпейдж и сайдбаров диспатчер данных для запроса pageData.
   * Сначала данные для Dialog, потом для Sidebar, потом для модалок **/
  const setDataRequest = (dataRequest: ViewParams) =>
    appDispatch((dispatch, getState) =>
      dispatchByActiveView(getState, {
        dialog: () => dispatch(setDialogViewData(dataRequest)),
        sidebar: () => dispatch(setSidebarViewData(dataRequest)),
        page: () => dispatch(setPageViewData(dataRequest)),
      }),
    );

  /** Функция для устрановки контекста диалога/сайдбара */
  const setModalContext = (context: FormContextValue) =>
    appDispatch((dispatch, getState) =>
      dispatchByActiveView(getState, {
        dialog: () => dispatch(setDialogContext(context)),
        sidebar: () => dispatch(setSidebarContext(context)),
      }),
    );

  /** Функция для открытия вьюхи в full-page варианте */
  const openPageView = (viewParams: ViewParams, crumb: Breadcrumb) => {
    appDispatch(
      openView({
        viewRequest: viewParams,
        breadcrumbs: { [viewParamsToString(viewParams)]: [crumb] },
      }),
    );

    /** Когда ЧТО-ТО открывает фуллпейдж вьюху из модалки => закрываем все модалки **/
    if (dialogs.length) {
      appDispatch(clearDialogsStack());
    }
  };

  /** Функция закрытия вьюхи (общий стек: [...fullPageViews, ...dialogViews]) */
  const closeView = () =>
    appDispatch((dispatch, getState) =>
      dispatchByActiveView(getState, {
        dialog: () => dispatch(closeDialog()),
        sidebar: () => dispatch(closeSidebar()),
        page: () => dispatch(closePageView()),
      }),
    );

  /** Функция закрытия сайдбара */
  const closeSidebar = () => appDispatch(closeSidebarView());

  /** Переход по стеку до указанной вьюхи и указанной крошки во вьюху */
  const setStackToView = useCallback(
    (viewRequest: ViewParams, breadcrumbs: PageAreaBreadcrumbs) => {
      const targetViewKey = viewParamsToString(viewRequest);
      const sliceIdx = viewStack.findIndex(
        (view) => viewParamsToString(view.viewRequest) === targetViewKey,
      );
      if (sliceIdx === -1) throw new Error(`Unexpected page to redirect: ${targetViewKey}`);

      const prevView = viewStack[sliceIdx];
      const newView = { ...prevView, breadcrumbs };
      const newStack = [...viewStack].slice(0, sliceIdx).concat(newView);

      appDispatch(setupViewStack(newStack));
    },

    [appDispatch, viewStack],
  );

  /** Инициализация стека при переходе по навигации **/
  const initializeStack = (views: Array<FormViewParams>) => {
    appDispatch(setupViewStack(views));
  };

  return {
    currentView,
    dialogs,
    sidebars,
    setDataRequest,
    initializeStack,
    setStackToView,
    openModalView,
    openSidebarView,
    setModalContext,
    openPageView,
    closeView,
    closeSidebar,
  };
};

export default useViewController;
